import React, { useState, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  Switch,
  FormControlLabel,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Divider,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  AutoAwesome as AutoAwesomeIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Close as CloseIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  AddPhotoAlternate as AddPhotoIcon,
  Assignment as AssignmentIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { generatedRamsApi } from '../services/api';
import GeneratedRAMSEditor from './GeneratedRAMSEditor';

const STEPS = ['Setup', 'Questions', 'Review & Save'];

const RAMSGeneratorSection = ({ projectId, currentUser, onGenerated, onDeleted }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Panel open/close
  const [expanded, setExpanded] = useState(false);

  // Wizard step (0 = setup, 1 = questions, 2 = review)
  const [activeStep, setActiveStep] = useState(0);

  // Loading / error state
  const [loading, setLoading] = useState(false);
  const [loadingPhase, setLoadingPhase] = useState('context'); // 'context' | 'questions' | 'rams'
  const [contextLoaded, setContextLoaded] = useState(false);
  const [error, setError] = useState(null);

  // Step 0 — Setup fields
  const [guidance, setGuidance] = useState('');
  const [availableImages, setAvailableImages] = useState([]); // from project files
  const [selectedImageIds, setSelectedImageIds] = useState(new Set());
  const [officeWorkers, setOfficeWorkers] = useState([]);
  const [siteWorkers, setSiteWorkers] = useState([]);
  const [allWorkers, setAllWorkers] = useState([]); // all workers for responsible people
  const [contractsManagerId, setContractsManagerId] = useState('');
  const [supervisorId, setSupervisorId] = useState('');
  const [responsiblePeopleIds, setResponsiblePeopleIds] = useState(new Set());
  const [hospitalFields, setHospitalFields] = useState({
    hospital_name: '',
    hospital_distance: '',
    hospital_address: '',
    hospital_opening_times: '',
    hospital_phone: '',
  });

  // Step 1 — Questions from LLM
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});

  // Step 2 — Generated data
  const [generatedData, setGeneratedData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [revising, setRevising] = useState(false);

  // Photo upload
  const photoInputRef = useRef(null);

  // ── Toggle & context load ──────────────────────────────────────────

  const handleToggle = useCallback(async () => {
    if (expanded) {
      setExpanded(false);
      return;
    }

    setExpanded(true);

    if (contextLoaded) return;

    setLoading(true);
    setLoadingPhase('context');
    setError(null);
    try {
      const res = await generatedRamsApi.getContext(projectId);
      const ctx = res.data;

      // Images
      const imgs = (ctx.request_images || []).map(img => ({
        ...img,
        selected: true,
      }));
      setAvailableImages(imgs);
      setSelectedImageIds(new Set(imgs.map(img => img.file_id)));

      // Workers
      const workers = ctx.workers || {};
      setOfficeWorkers(workers.office_workers || []);
      setSiteWorkers(workers.site_workers || []);
      setContractsManagerId(workers.default_contracts_manager_id || '');
      setSupervisorId(workers.default_supervisor_id || '');

      // All workers for responsible people selection
      const allW = [...(workers.office_workers || []), ...(workers.site_workers || [])];
      setAllWorkers(allW);
      setResponsiblePeopleIds(
        new Set((workers.assigned_worker_ids || []).map(workerId => String(workerId)))
      );

      // Hospital
      setHospitalFields(ctx.hospital || {
        hospital_name: '',
        hospital_distance: '',
        hospital_address: '',
        hospital_opening_times: '',
        hospital_phone: '',
      });

      setContextLoaded(true);
    } catch (err) {
      console.error('Failed to load RAMS context:', err);
      setError('Failed to load project context. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [expanded, contextLoaded, projectId]);

  // ── Image selection ────────────────────────────────────────────────

  const toggleImage = useCallback((fileId) => {
    setSelectedImageIds(prev => {
      const next = new Set(prev);
      if (next.has(fileId)) {
        next.delete(fileId);
      } else {
        next.add(fileId);
      }
      return next;
    });
  }, []);

  const handleAddPhotos = useCallback((e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const dataUrl = ev.target.result;
        const fakeId = `upload_${Date.now()}_${Math.random().toString(36).slice(2)}`;
        const newImg = {
          file_id: fakeId,
          file_name: file.name,
          file_type: file.type,
          url: dataUrl,
          data_url: dataUrl,
          source: 'uploaded',
        };
        setAvailableImages(prev => [...prev, newImg]);
        setSelectedImageIds(prev => new Set([...prev, fakeId]));
      };
      reader.readAsDataURL(file);
    });
    // Reset input so same file can be re-added
    e.target.value = '';
  }, []);

  const selectedImages = useMemo(
    () => availableImages.filter(img => selectedImageIds.has(img.file_id)),
    [availableImages, selectedImageIds]
  );

  // ── Hospital field change ──────────────────────────────────────────

  const handleHospitalChange = useCallback((key, value) => {
    setHospitalFields(prev => ({ ...prev, [key]: value }));
  }, []);

  // ── Responsible people toggle ─────────────────────────────────────

  const toggleResponsiblePerson = useCallback((workerId) => {
    setResponsiblePeopleIds(prev => {
      const next = new Set(prev);
      const normalizedWorkerId = String(workerId);
      if (next.has(normalizedWorkerId)) {
        next.delete(normalizedWorkerId);
      } else {
        next.add(normalizedWorkerId);
      }
      return next;
    });
  }, []);

  // ── Step 0 → Step 1: Start generation ─────────────────────────────

  const handleStart = useCallback(async () => {
    setLoading(true);
    setLoadingPhase('questions');
    setError(null);
    try {
      const res = await generatedRamsApi.start(projectId, {
        guidance_text: guidance,
        guidance_images: selectedImages.map(img => ({
          file_id: img.file_id,
          file_name: img.file_name,
          source: img.source || 'project_file',
          ...(img.data_url ? { data_url: img.data_url } : {}),
        })),
      });
      const qList = res.data?.questions || [];
      setQuestions(qList);
      // Initialise answers with empty values
      const init = {};
      qList.forEach(q => {
        if (q.type === 'boolean') {
          init[q.id] = false;
        } else {
          init[q.id] = '';
        }
      });
      setAnswers(init);
      setActiveStep(1);
    } catch (err) {
      console.error('Failed to start RAMS generation:', err);
      setError(err?.response?.data?.error || 'Failed to start AI generation. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [projectId, guidance, selectedImages]);

  // ── Step 1 → Step 2: Generate RAMS ────────────────────────────────

  const handleGenerate = useCallback(async () => {
    setLoading(true);
    setLoadingPhase('rams');
    setError(null);
    try {
      const res = await generatedRamsApi.generate(projectId, {
        context: {
          guidance_text: guidance,
          guidance_images: selectedImages.map(img => ({
            file_id: img.file_id,
            file_name: img.file_name,
            source: img.source || 'project_file',
            ...(img.data_url ? { data_url: img.data_url } : {}),
          })),
        },
        answers,
      });
      setGeneratedData(res.data);
      setActiveStep(2);
    } catch (err) {
      console.error('Failed to generate RAMS:', err);
      setError(err?.response?.data?.error || 'Failed to generate RAMS content. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [projectId, guidance, selectedImages, answers]);

  // ── Step 2: Save ──────────────────────────────────────────────────

  const handleSave = useCallback(async () => {
    if (!generatedData) return;
    setSaving(true);
    setError(null);
    try {
      await generatedRamsApi.save(projectId, {
        method_statements: generatedData.method_statements,
        risk_assessment: generatedData.risk_assessment,
        plant_equipment: generatedData.plant_equipment,
        contracts_manager_worker_id: contractsManagerId || null,
        supervisor_worker_id: supervisorId || null,
        responsible_people_worker_ids: Array.from(responsiblePeopleIds).join(','),
        ...hospitalFields,
      });
      // Reset wizard
      setExpanded(false);
      setActiveStep(0);
      setContextLoaded(false);
      setGeneratedData(null);
      setGuidance('');
      setQuestions([]);
      setAnswers({});
      if (onGenerated) onGenerated();
    } catch (err) {
      console.error('Failed to save RAMS:', err);
      setError(err?.response?.data?.error || 'Failed to save generated RAMS.');
    } finally {
      setSaving(false);
    }
  }, [generatedData, projectId, contractsManagerId, supervisorId, responsiblePeopleIds, hospitalFields, onGenerated]);

  // ── Step 2: Revise via AI ─────────────────────────────────────────

  const handleRevise = useCallback(async (comments) => {
    if (!generatedData) return;
    setRevising(true);
    setError(null);
    try {
      // If the generated RAMS has not been saved yet, call the generate backend
      // with the existing data + revision comments.
      // We use a client-side approach: call a separate endpoint or pass the data directly.
      // Since we built a revise route that requires an existing ID, we need to adapt.
      // For unsaved data, we'll re-call generate with context including the comments.
      // But the spec says post-generation UX lets user revise or delete — revise
      // works on already-generated data. Let's send the current data + comments
      // to a generate-like call.
      const res = await generatedRamsApi.generate(projectId, {
        context: {
          guidance_text: guidance + '\n\nREVISION REQUEST: ' + comments,
          guidance_images: selectedImages.map(img => ({
            file_id: img.file_id,
            file_name: img.file_name,
            source: 'project_file',
          })),
          existing_data: generatedData,
        },
        answers,
      });
      setGeneratedData(res.data);
    } catch (err) {
      console.error('Failed to revise RAMS:', err);
      setError(err?.response?.data?.error || 'Failed to revise RAMS. Please try again.');
    } finally {
      setRevising(false);
    }
  }, [generatedData, projectId, guidance, selectedImages, answers]);

  // ── Cancel / back ─────────────────────────────────────────────────

  const handleCancel = useCallback(() => {
    setExpanded(false);
    setActiveStep(0);
    setError(null);
  }, []);

  const handleBack = useCallback(() => {
    setActiveStep(prev => Math.max(0, prev - 1));
    setError(null);
  }, []);

  // ── Answer change ─────────────────────────────────────────────────

  const handleAnswerChange = useCallback((questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  }, []);

  // ── Render question input ─────────────────────────────────────────

  const renderQuestionInput = useCallback((question) => {
    const value = answers[question.id] ?? '';

    switch (question.type) {
      case 'textarea':
        return (
          <TextField
            key={question.id}
            fullWidth
            multiline
            minRows={3}
            maxRows={6}
            label={question.label}
            placeholder={question.placeholder || ''}
            helperText={question.help_text || ''}
            value={value}
            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
            required={question.required}
            size="small"
          />
        );

      case 'number':
        return (
          <TextField
            key={question.id}
            fullWidth
            type="number"
            label={question.label}
            placeholder={question.placeholder || ''}
            helperText={question.help_text || ''}
            value={value}
            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
            required={question.required}
            size="small"
          />
        );

      case 'select':
        return (
          <FormControl key={question.id} fullWidth size="small">
            <InputLabel>{question.label}</InputLabel>
            <Select
              value={value}
              onChange={(e) => handleAnswerChange(question.id, e.target.value)}
              label={question.label}
            >
              {(question.options || []).map(opt => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </Select>
            {question.help_text && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                {question.help_text}
              </Typography>
            )}
          </FormControl>
        );

      case 'boolean':
        return (
          <FormControlLabel
            key={question.id}
            control={
              <Switch
                checked={!!value}
                onChange={(e) => handleAnswerChange(question.id, e.target.checked)}
              />
            }
            label={question.label}
          />
        );

      default: // text
        return (
          <TextField
            key={question.id}
            fullWidth
            label={question.label}
            placeholder={question.placeholder || ''}
            helperText={question.help_text || ''}
            value={value}
            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
            required={question.required}
            size="small"
          />
        );
    }
  }, [answers, handleAnswerChange]);

  // ── Don't render for non-Office users ─────────────────────────────

  if (currentUser?.role !== 'Office') return null;

  // ── Main render ───────────────────────────────────────────────────

  return (
    <Box sx={{ mb: 3 }}>
      {/* Collapsed trigger — full width, single row */}
      <Paper
        variant="outlined"
        onClick={!expanded ? handleToggle : undefined}
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 2,
          py: 1.5,
          cursor: expanded ? 'default' : 'pointer',
          '&:hover': !expanded ? { bgcolor: 'action.hover' } : {},
          borderBottomLeftRadius: expanded ? 0 : undefined,
          borderBottomRightRadius: expanded ? 0 : undefined,
          borderBottom: expanded ? 0 : undefined,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography fontWeight="medium">Create RAMS (Beta)</Typography>
        <AutoAwesomeIcon fontSize="small" color="primary" sx={{ ml: 0.5 }} />
        </Box>
        {!expanded ? <ExpandMoreIcon fontSize="small" color="action" /> : (
          <IconButton size="small" onClick={handleCancel} sx={{ mr: -1 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Paper>

      <Collapse in={expanded} timeout="auto">
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, borderTopLeftRadius: 0, borderTopRightRadius: 0 }}>
          {/* Stepper header */}
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>Create RAMS</Typography>

          {/* Stepper */}
          <Stepper activeStep={activeStep} sx={{ mb: 3 }} alternativeLabel={isMobile}>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, py: 4 }}>
              <CircularProgress />
              <Typography color="text.secondary">
                {loadingPhase === 'context'
                  ? 'Loading project context...'
                  : loadingPhase === 'questions'
                    ? 'Generating follow-up questions...'
                    : 'Generating RAMS content...'}
              </Typography>
            </Box>
          )}

          {/* Step 0: Setup */}
          {!loading && activeStep === 0 && contextLoaded && (
            <Box>
              <TextField
                fullWidth
                multiline
                minRows={3}
                maxRows={6}
                label="Additional guidance, if nothing is provided just the existing project information will be used to draft RAMS."
                placeholder="Describe the scope of works, specific hazards, access requirements, or anything else the AI should consider..."
                value={guidance}
                onChange={(e) => setGuidance(e.target.value)}
                sx={{ mb: 3 }}
              />

              {/* Image selection */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                  <Typography variant="subtitle2">
                    Photos ({selectedImageIds.size} of {availableImages.length} selected)
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddPhotoIcon />}
                    onClick={() => photoInputRef.current?.click()}
                    variant="outlined"
                    sx={{ textTransform: 'none' }}
                  >
                    Add Photos
                  </Button>
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: 'none' }}
                    onChange={handleAddPhotos}
                  />
                </Box>
                {availableImages.length > 0 ? (
                  <>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      Click to include/exclude from AI analysis
                    </Typography>
                    <ImageList cols={isMobile ? 2 : 5} rowHeight={110} gap={6}>
                      {availableImages.map((img) => {
                        const isSelected = selectedImageIds.has(img.file_id);
                        return (
                          <ImageListItem
                            key={img.file_id}
                            onClick={() => toggleImage(img.file_id)}
                            sx={{
                              cursor: 'pointer',
                              border: isSelected ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
                              borderRadius: 1,
                              opacity: isSelected ? 1 : 0.45,
                              transition: 'all 0.15s',
                              overflow: 'hidden',
                              '&:hover': { opacity: isSelected ? 1 : 0.75 },
                            }}
                          >
                            <img
                              src={img.data_url || img.url}
                              alt={img.file_name}
                              loading="lazy"
                              style={{ objectFit: 'cover', height: '100%', width: '100%' }}
                            />
                            <ImageListItemBar
                              title={img.file_name}
                              sx={{ '& .MuiImageListItemBar-title': { fontSize: '0.7rem' } }}
                            />
                          </ImageListItem>
                        );
                      })}
                    </ImageList>
                  </>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    No project photos yet. Use "Add Photos" to include images for analysis.
                  </Typography>
                )}
              </Box>

              {/* Workers */}
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 3 }}>
                <FormControl size="small" sx={{ flex: 1 }}>
                  <InputLabel>Contracts Manager</InputLabel>
                  <Select
                    value={contractsManagerId}
                    onChange={(e) => setContractsManagerId(e.target.value)}
                    label="Contracts Manager"
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {officeWorkers.map(w => (
                      <MenuItem key={w.worker_id} value={w.worker_id}>{w.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ flex: 1 }}>
                  <InputLabel>Supervisor</InputLabel>
                  <Select
                    value={supervisorId}
                    onChange={(e) => setSupervisorId(e.target.value)}
                    label="Supervisor"
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {siteWorkers.map(w => (
                      <MenuItem key={w.worker_id} value={w.worker_id}>{w.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Hospital */}
              <Typography variant="subtitle2" gutterBottom>
                Nearest Hospital (auto-detected from project postcode)
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 3, flexWrap: 'wrap' }}>
                <TextField size="small" label="Name" value={hospitalFields.hospital_name} onChange={(e) => handleHospitalChange('hospital_name', e.target.value)} sx={{ flex: 2, minWidth: 180 }} />
                <TextField size="small" label="Distance" value={hospitalFields.hospital_distance} onChange={(e) => handleHospitalChange('hospital_distance', e.target.value)} sx={{ flex: 1, minWidth: 100 }} />
                <TextField size="small" label="Address" value={hospitalFields.hospital_address} onChange={(e) => handleHospitalChange('hospital_address', e.target.value)} sx={{ flex: 2, minWidth: 180 }} />
                <TextField size="small" label="Opening Times" value={hospitalFields.hospital_opening_times} onChange={(e) => handleHospitalChange('hospital_opening_times', e.target.value)} sx={{ flex: 1, minWidth: 140 }} />
                <TextField size="small" label="Phone" value={hospitalFields.hospital_phone} onChange={(e) => handleHospitalChange('hospital_phone', e.target.value)} sx={{ flex: 1, minWidth: 120 }} />
              </Box>

              {/* Responsible People */}
              {allWorkers.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Responsible People ({responsiblePeopleIds.size} selected)
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Select personnel to include in the Responsible Persons section of the RAMS
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {allWorkers.map(w => (
                      <Chip
                        key={w.worker_id}
                        label={w.name}
                        size="small"
                        variant={responsiblePeopleIds.has(String(w.worker_id)) ? 'filled' : 'outlined'}
                        color={responsiblePeopleIds.has(String(w.worker_id)) ? 'primary' : 'default'}
                        onClick={() => toggleResponsiblePerson(w.worker_id)}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Disclaimer */}
              <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
                <Typography variant="caption">
                  <b>Disclaimer:</b> Struxor makes no guarantees regarding the quality or
                  accuracy of AI-generated outputs. All generated RAMS content should be
                  carefully reviewed by qualified personnel before use. By continuing to use
                  this tool you acknowledge and accept this.
                </Typography>
              </Alert>

              {/* Link to RAMS Config */}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Configure emergency contacts, general precautions, and other RAMS defaults in{' '}
                <Button
                  component={RouterLink}
                  to="/workspace?tab=rams-config"
                  target="_blank"
                  rel="noopener noreferrer"
                  size="small"
                  endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                  sx={{ textTransform: 'none', fontWeight: 600, p: 0, minWidth: 'auto', verticalAlign: 'baseline' }}
                >
                  RAMS Config
                </Button>
              </Typography>

              {/* Actions */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button onClick={handleCancel}>Cancel</Button>
                <Button
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  onClick={handleStart}
                  disabled={loading}
                >
                  Start Generation
                </Button>
              </Box>
            </Box>
          )}

          {/* Step 1: Questions */}
          {!loading && activeStep === 1 && (
            <Box>
              {questions.length === 0 ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  The AI didn't need any additional information. Click "Generate RAMS" to continue.
                </Alert>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Please answer these follow-up questions to help generate the best RAMS for this project.
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                    {questions.map(q => renderQuestionInput(q))}
                  </Box>
                </>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                <Button startIcon={<ArrowBackIcon />} onClick={handleBack}>
                  Back
                </Button>
                <Button
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  onClick={handleGenerate}
                  disabled={loading}
                >
                  Generate RAMS
                </Button>
              </Box>
            </Box>
          )}

          {/* Step 2: Review & Save */}
          {!loading && activeStep === 2 && generatedData && (
            <Box>
              <GeneratedRAMSEditor
                data={generatedData}
                officeWorkers={officeWorkers}
                siteWorkers={siteWorkers}
                contractsManagerId={contractsManagerId}
                supervisorId={supervisorId}
                responsiblePeopleIds={Array.from(responsiblePeopleIds).map(id => String(id))}
                hospitalFields={hospitalFields}
                onDataChange={setGeneratedData}
                onHospitalChange={handleHospitalChange}
                onContractsManagerChange={setContractsManagerId}
                onSupervisorChange={setSupervisorId}
                onResponsiblePeopleChange={(ids) => setResponsiblePeopleIds(new Set(ids))}
                onSave={handleSave}
                onRevise={handleRevise}
                saving={saving}
                revising={revising}
                isNew={true}
              />

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                <Button startIcon={<ArrowBackIcon />} onClick={handleBack}>
                  Back to Questions
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      </Collapse>
    </Box>
  );
};

export default RAMSGeneratorSection;
